import { useState } from "react";
import { Loader2, Send, CircleCheck, CircleX } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui-kit/Card";
import { fmtBytes } from "@/pages/settings/utils";

function EmailEditor() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    const to = email.trim();
    if (!to) { setResult({ ok: false, text: "Enter a recipient email address." }); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/email/test", { recipient_email: to });
      setResult({
        ok: true,
        text: `Sent via Resend (id ${r.data.id}).`,
        detail: `From ${r.data.from} · reply-to ${r.data.reply_to} · PDF "${r.data.attachment_filename}" (${fmtBytes(r.data.attachment_bytes)}) attached.`,
      });
    } catch (e) {
      setResult({ ok: false, text: formatApiErrorDetail(e) || "Send failed." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Label htmlFor="resend-test-email">Recipient address</Label>
          <Input id="resend-test-email" data-testid="resend-test-email-input" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="you@firm.com"
            onKeyDown={(e) => e.key === "Enter" && send()} />
        </div>
        <Button type="button" variant="secondary" onClick={send} disabled={busy} data-testid="resend-test-email-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test email
        </Button>
      </div>
      <p className="-mt-1 text-xs text-muted-foreground">
        Sends a short test message from <code className="rounded bg-secondary px-1">invoices@billing.mattertab.com</code> with
        a small one-page PDF attached, to prove outbound email works. Manual only — nothing is sent automatically.
      </p>
      {result && (
        <div data-testid="resend-test-result"
          className={cn("flex items-start gap-2 rounded-md border px-3 py-2 text-sm",
            result.ok ? "border-status-paid/40 bg-status-paid/10 text-status-paid"
                      : "border-status-void/40 bg-status-void/10 text-status-void")}>
          {result.ok ? <CircleCheck className="mt-0.5 h-4 w-4 shrink-0" /> : <CircleX className="mt-0.5 h-4 w-4 shrink-0" />}
          <div>
            <p className="font-medium">{result.text}</p>
            {result.detail && <p className="mt-0.5 text-foreground/70">{result.detail}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export function ResendTestCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Email (Resend)</CardTitle>
        <CardDescription>Outbound email is sent through Resend from the verified domain billing.mattertab.com. Send yourself a test email (with a sample PDF attached) to confirm delivery before the funder-submission action goes live.</CardDescription>
      </CardHeader>
      <CardContent><EmailEditor /></CardContent>
    </Card>
  );
}
